import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaBoxOpen, FaFilter, FaFileExport, FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function CategorySales() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [categorySales, setCategorySales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    const fetchCategorySales = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getVendorCategorySales/${vendorId}`);
        if (res.data.success) {
          setCategorySales(res.data.categorySales || []);
        } else {
          setCategorySales([]);
        }
      } catch (err) {
        console.error("Error fetching category sales:", err);
        setCategorySales([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchCategorySales();
  }, [vendorId]);

  const toggleCategory = (catId) => {
    setExpandedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleSubcategory = (subcatId) => {
    setExpandedSubcategories((prev) => ({ ...prev, [subcatId]: !prev[subcatId] }));
  };

  // ----------------- Export to Excel -----------------
  const exportToExcel = () => {
    const data = [];
    categorySales.forEach((cat) => {
      cat.subcategories.forEach((sub) => {
        sub.products.forEach((p) => {
          data.push({
            "Category": cat.category_name,
            "Subcategory": sub.subcategory_name,
            "Product Name": p.name,
            SKU: p.sku,
            Price: p.price,
            MRP: p.mrp,
            "Quantity Sold": p.quantity_sold,
            "Total Revenue": p.total_revenue,
          });
        });
      });
    });

    if (!data.length) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Category Sales");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Category_Sales_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> Category Sales Report
            </h4>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success d-flex align-items-center gap-2"
                onClick={exportToExcel}
              >
                <FaFileExport /> Export Excel
              </button>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FaFilter /> Filters
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label>Search Product</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Product name or SKU"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-primary w-100"
                    onClick={() => setShowFilter(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Category Sales Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4 text-muted">Loading category sales...</div>
              ) : categorySales.length > 0 ? (
                <div className="accordion" id="categorySalesAccordion">
                  {categorySales.map((cat, ci) => (
                    <div key={cat.category_id} className="mb-2">
                      <button
                        className="btn btn-outline-primary w-100 d-flex justify-content-between align-items-center"
                        onClick={() => toggleCategory(cat.category_id)}
                      >
                        <span>{cat.category_name}</span>
                        {expandedCategories[cat.category_id] ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                      {expandedCategories[cat.category_id] && (
                        <div className="mt-2 ps-3">
                          {cat.subcategories.map((sub, si) => (
                            <div key={sub.subcategory_id} className="mb-2">
                              <button
                                className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                                onClick={() => toggleSubcategory(sub.subcategory_id)}
                              >
                                <span>{sub.subcategory_name}</span>
                                {expandedSubcategories[sub.subcategory_id] ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                              {expandedSubcategories[sub.subcategory_id] && (
                                <div className="table-responsive mt-2 ps-3">
                                  <table className="table table-hover align-middle">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Image</th>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>Price</th>
                                        <th>MRP</th>
                                        <th>Quantity Sold</th>
                                        <th>Total Revenue</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sub.products
                                        .filter((p) =>
                                          `${p.name} ${p.sku}`
                                            .toLowerCase()
                                            .includes(filters.search.toLowerCase())
                                        )
                                        .map((p, pi) => (
                                          <tr key={p.product_id}>
                                            <td>{pi + 1}</td>
                                            <td>
                                              <img
                                                src={p.featured_image ? `${imgurl}${p.featured_image}` : "/noimage.png"}
                                                alt={p.name}
                                                style={{
                                                  width: "45px",
                                                  height: "45px",
                                                  borderRadius: "6px",
                                                  objectFit: "cover",
                                                }}
                                              />
                                            </td>
                                            <td className="fw-semibold">{p.name}</td>
                                            <td>{p.sku}</td>
                                            <td>₹{p.price}</td>
                                            <td>₹{p.mrp}</td>
                                            <td>{p.quantity_sold}</td>
                                            <td>₹{p.total_revenue}</td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">No category sales data available</div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .table td,
        .table th {
          vertical-align: middle;
        }
      `}</style>
    </>
  );
}

export default CategorySales;
